import { useState } from "react";
import { ExternalLink, ShieldCheck, WalletCards } from "lucide-react";
import type {
  HermesFinancialBookV1,
  HermesFinancialMoneyMetricV1,
  HermesFinancialReportV1,
} from "@tradvera/contracts";

export function FinancialReportPocket({ ar, report, busy }: {
  ar: boolean;
  report: HermesFinancialReportV1;
  busy: boolean;
}) {
  const t = (arabic: string, english: string) => ar ? arabic : english;
  const [selectedBookId, setSelectedBookId] = useState(report.preferredBookId);
  const book = report.books.find((item) => item.bookId === selectedBookId)
    ?? report.books.find((item) => item.bookId === report.preferredBookId)
    ?? report.books[0]
    ?? null;
  const money = (metric: HermesFinancialMoneyMetricV1 | undefined) => {
    if (!metric || metric.value === null) return t("غير متاح — ليس صفرًا", "Unavailable — not zero");
    const amount = Number(metric.value);
    const formatted = Number.isFinite(amount)
      ? new Intl.NumberFormat(ar ? "ar-EG" : "en-US", { style: "currency", currency: metric.currency, maximumFractionDigits: 2 }).format(amount)
      : `${metric.value} ${metric.currency}`;
    return metric.state === "PROVISIONAL" ? `${formatted} · ${t("مبدئي", "Provisional")}` : formatted;
  };
  const kindLabel = (item: HermesFinancialBookV1) => item.kind === "OWNED_PRIVATE"
    ? t("حقيقية", "Real") : t("افتراضية", "Virtual");

  return <section className="portfolio-pocket financial-report-pocket">
    <div className="companion-section-head"><div><small>HERMES WALLET BRIDGE</small><h1>{t("تقريرك المالي", "Your financial report")}</h1></div><WalletCards/></div>
    <p className="section-lead">{t("نفس التقرير الموثق الظاهر هنا يصل إلى هيرمس التنفيذي من الخادم مباشرة. كل محفظة مستقلة، والقيمة المفقودة تظهر كغير متاحة وليست صفرًا.", "The same verified report shown here reaches Executive Hermes directly from the server. Every book stays separate, and a missing value is unavailable—not zero.")}</p>
    {report.books.length > 0 && <div className="portfolio-book-tabs" role="tablist" aria-label={t("اختر المحفظة", "Choose portfolio")}>
      {report.books.map((item) => <button key={item.bookId} role="tab" aria-selected={item.bookId === book?.bookId} className={item.bookId === book?.bookId ? "is-active" : ""} onClick={() => setSelectedBookId(item.bookId)}>
        <b>{kindLabel(item)}</b><small>{item.connectionState === "CONNECTED" ? t("مربوطة", "Connected") : item.connectionState === "CASH_ONLY" ? t("كاش فقط", "Cash only") : t("غير مربوطة", "Unbound")}</small>
      </button>)}
    </div>}
    {book ? <div className="portfolio-mode-card">
      <span className={book.connectionState === "CONNECTED" ? "is-connected" : "is-unbound"}>{busy ? "SYNCING" : `${book.kind} · ${book.connectionState}`}</span>
      <div className="portfolio-pocket-balance">
        <small>{book.kind === "OWNED_PRIVATE" ? t("القيمة الحالية — أموال حقيقية", "Current equity — real owner funds") : t("القيمة الحالية — أموال افتراضية", "Current equity — virtual money")}</small>
        <strong>{busy ? "…" : money(book.currentEquity)}</strong>
        <em>{book.kind === "OWNED_PRIVATE" ? "OWNER FUNDS · READ ONLY" : "PAPER · VIRTUAL · REAL MONEY: NO"}</em>
      </div>
      <div className="portfolio-pocket-kpis">
        <div><small>{t("الكاش المتاح", "Available cash")}</small><b>{money(book.availableCash)}</b></div>
        <div><small>{t("المحقق قبل الضريبة", "Realized before tax")}</small><b>{money(book.realizedNetPnlBeforeTax)}</b></div>
        <div><small>{t("غير المحقق", "Unrealized")}</small><b>{money(book.unrealizedPnlBeforeExitCosts)}</b></div>
      </div>
      <div className="portfolio-report-facts">
        <div><small>{t("المراكز المفتوحة", "Open positions")}</small><b>{book.openPositionsCount}</b></div>
        <div><small>{t("حداثة البيانات", "Freshness")}</small><b>{book.freshness}</b></div>
        <div><small>{t("طريقة المصدر", "Source mode")}</small><b>{book.sourceMode}</b></div>
      </div>
      <div className="portfolio-pocket-truth">
        <ShieldCheck size={16}/><span><b>{t("هيرمس يقرأ هذا التقرير من الخادم", "Hermes reads this server-side report")}</b><small>{book.asOf ? new Date(book.asOf).toLocaleString(ar ? "ar-EG" : "en-GB") : t("لم يصل تاريخ موثوق من المصدر", "No trusted source timestamp yet")}</small></span>
      </div>
      <div className="portfolio-report-safeguard"><ShieldCheck size={15}/><span>{t("لا خلط بين الحقيقي والافتراضي · لا تنفيذ لدى البروكر · لا ثقة في أرقام يرسلها المتصفح", "No real/virtual mixing · no broker execution · no trust in browser-supplied balances")}</span></div>
      <a href="/?world=trade"><ExternalLink size={16}/>{t("افتح منصة التداول الكاملة", "Open full Trade platform")}</a>
    </div> : <div className="companion-empty"><WalletCards size={28}/><b>{t("لا توجد محفظة نشطة", "No active portfolio")}</b><span>{t("لن نعرض أصفارًا بديلة عن البيانات الناقصة.", "Missing data will not be replaced with zeros.")}</span></div>}
  </section>;
}
